import React, { useEffect, useState } from 'react'
import { Row, Col, Button, Input, Card } from "reactstrap";
import ReactTable from 'react-table';
import LoadingOverlay from 'react-loading-overlay';
import Loader from 'react-loaders';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSync, faDownload } from "@fortawesome/free-solid-svg-icons";
import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { FaCalendarAlt } from 'react-icons/fa';
import FetchFbaInventorySummaryService from '../../../../../../Service/DashboardServices/FBAInventoryServices/FetchFbaInventorySummaryService';
import RefreshFbaInventorySummaryService from '../../../../../../Service/DashboardServices/FBAInventoryServices/RefreshFbaInventorySummaryService';
import InventoryAlertsPage from './InventoryAlerts/InventoryAlertsPage.js';

function StoreOverviewFBAInventory(props) {
  const [activeView, setActiveView] = useState("alerts");
  const [inventoryList, setInventoryList] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [appliedRange, setAppliedRange] = useState({ startDate: null, endDate: null });
  const formatDate = (date) =>
    date
      ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
      : null;

  const fetchInventory = () => {
    if (!props.profileId) {
      setInventoryList([]);
      return;
    }
    setLoading(true);
    const data = {
      integerID: props.profileId,
      startDate: formatDate(appliedRange.startDate),
      endDate: formatDate(appliedRange.endDate),
    };
    FetchFbaInventorySummaryService(props.token, data).then((response) => {
      setInventoryList(Array.isArray(response) ? response : []);
      setLoading(false);
    });
  };

  const refreshInventory = () => {
    if (!props.profileId) return;
    setLoading(true);
    const data = {
      integerID: props.profileId,
      startDate: formatDate(appliedRange.startDate),
      endDate: formatDate(appliedRange.endDate),
    };
    RefreshFbaInventorySummaryService(props.token, data).then((response) => {
      if (Array.isArray(response)) setInventoryList(response);
      setLoading(false);
    });
  };

  useEffect(() => {
    if (activeView === "table") {
      fetchInventory();
    }
  }, [props.profileId, appliedRange, activeView]);

  const handleSubmit = () => setAppliedRange({ startDate, endDate });

  const handleDateChange = (setter) => (date) => {
    setter(date);
    if (!date && (appliedRange.startDate || appliedRange.endDate)) {
      setAppliedRange({ startDate: null, endDate: null });
    }
  };

  const latestCreatedAt = inventoryList.reduce((latest, item) => {
    const value = item.created_at ? String(item.created_at) : null;
    return value && (!latest || value > latest) ? value : latest;
  }, null);
  const snapshotText = latestCreatedAt
    ? `${latestCreatedAt.slice(5, 7)}-${latestCreatedAt.slice(8, 10)}-${latestCreatedAt.slice(0, 4)} ${latestCreatedAt.slice(11, 16)}`
    : null;

  const search = searchText.trim().toLowerCase();
  const rangeStart = formatDate(appliedRange.startDate);
  const rangeEnd = formatDate(appliedRange.endDate);
  const filteredInventory = inventoryList.filter((item) => {
    if (rangeStart || rangeEnd) {
      const value = item.last_updated || item.created_at;
      if (!value) return false;
      const itemDate = String(value).slice(0, 10);
      if (rangeStart && itemDate < rangeStart) return false;
      if (rangeEnd && itemDate > rangeEnd) return false;
    }
    return (!search ||
      Object.values(item).some((value) =>
        String(value == null ? "" : value).toLowerCase().includes(search)
      ));
  });

  const textCell = (cell) => (
    <div className="text-truncate" title={cell.value}>{cell.value}</div>
  );
  const centerCell = (cell) => (
    <div className="text-center">{cell.value}</div>
  );
  const dateCell = (cell) => {
    if (!cell.value) return <div className="text-center"></div>;
    const value = String(cell.value);
    return (
      <div className="text-center">
        <div className="d-inline-block text-left">
          <div>{`${value.slice(5, 7)}-${value.slice(8, 10)}-${value.slice(0, 4)}`}</div>
          <div>{value.slice(11, 16)}</div>
        </div>
      </div>
    );
  };

  const columns = [
    { Header: "Amazon Last Updated", accessor: "last_updated", minWidth: 160, Cell: dateCell },
    { Header: "ASIN", accessor: "asin", minWidth: 110, Cell: textCell },
    { Header: "SKU", accessor: "sku", minWidth: 120, Cell: textCell },
    { Header: "FNSKU", accessor: "fnsku", minWidth: 110, Cell: textCell },
    { Header: "Product Name", accessor: "product_name", minWidth: 220, Cell: textCell },
    { Header: "Fulfillable", accessor: "fulfillable", Cell: centerCell },
    { Header: "Unfulfillable", accessor: "unfulfillable", Cell: centerCell },
    { Header: "Researching", accessor: "researching", Cell: centerCell },
    { Header: "Total Quantity", accessor: "total_quantity", Cell: centerCell },
    { Header: "Inbound Working", accessor: "inbound_working", Cell: centerCell },
    { Header: "Inbound Shipped", accessor: "inbound_shipped", Cell: centerCell },
    { Header: "Inbound Receiving", accessor: "inbound_receiving", Cell: centerCell },
    { Header: "Reserved Total", accessor: "reserved_total", Cell: centerCell },
    { Header: "Reserved Pending Customer Order", accessor: "reserved_pending_customer_order", minWidth: 130, Cell: centerCell },
    { Header: "Reserved FC Transfer", accessor: "reserved_fc_transfer", Cell: centerCell },
    { Header: "Reserved FC Processing", accessor: "reserved_fc_processing", Cell: centerCell },
  ];

  const exportColumns = [
    ["ID", "id"],
    ["Seller Partner ID", "sellerPartnerID"],
    ["Marketplace ID", "marketplaceId"],
    ["Integer ID", "integerID"],
    ["SKU", "sku"],
    ["ASIN", "asin"],
    ["FNSKU", "fnsku"],
    ["Product Name", "product_name"],
    ["Fulfillable", "fulfillable"],
    ["Unfulfillable", "unfulfillable"],
    ["Researching", "researching"],
    ["Total Quantity", "total_quantity"],
    ["Inbound Working", "inbound_working"],
    ["Inbound Shipped", "inbound_shipped"],
    ["Inbound Receiving", "inbound_receiving"],
    ["Reserved Total", "reserved_total"],
    ["Reserved Pending Customer Order", "reserved_pending_customer_order"],
    ["Reserved FC Transfer", "reserved_fc_transfer"],
    ["Reserved FC Processing", "reserved_fc_processing"],
    ["Amazon Last Updated", "last_updated"],
    ["Created At", "created_at"],
  ];

  const downloadInventory = () => {
    const rows = filteredInventory.map((item) =>
      exportColumns.reduce((row, [header, key]) => {
        row[header] = item[key] == null ? "" : item[key];
        return row;
      }, {})
    );
    const ws = XLSX.utils.json_to_sheet(rows, { header: exportColumns.map(([header]) => header) });
    const wb = { Sheets: { 'FBA Inventory': ws }, SheetNames: ['FBA Inventory'] };
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
    FileSaver.saveAs(data, `FBA_Inventory_${(props.storeName || props.profileId || "").toString().replace(/[\\/:*?"<>|]/g, "").trim()}_${formatDate(new Date())}.xlsx`);
  };

  const renderDatePicker = (label, selected, onChange, extraProps) => (
    <div className="mr-2" style={{ width: "180px" }}>
      <small className="d-block mb-1">{label}</small>
      <div className="position-relative">
        <FaCalendarAlt
          style={{
            position: "absolute",
            left: "10px",
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 1,
            pointerEvents: "none",
            color: "#495057",
          }}
        />
        <DatePicker
          selected={selected}
          onChange={onChange}
          wrapperClassName="w-100"
          dateFormat="MM/dd/yyyy"
          placeholderText={label}
          {...extraProps}
          customInput={<input className="form-control" style={{ paddingLeft: "32px" }} />}
        />
      </div>
    </div>
  );

  return (
    <div className="w-100">
      <div className="d-flex mb-3 mt-3 align-items-center" style={{ gap: "8px" }}>
        <Button
          type="button"
          className={`btn-icon btn-icon-left ${activeView === "alerts" ? "btn-background-color active" : "btn-secondary"}`}
          style={{
            fontWeight: 600,
            borderRadius: "6px",
            padding: "8px 18px",
            fontSize: "13px",
            border: activeView === "alerts" ? "1px solid #9ee04e" : "1px solid #ced4da",
            backgroundColor: activeView === "alerts" ? "#b1fa63" : "#f8f9fa",
            color: "#052847",
            boxShadow: activeView === "alerts" ? "0 2px 4px rgba(0,0,0,0.08)" : "none",
          }}
          onClick={() => setActiveView("alerts")}
        >
          Inventory Alerts & Operations
        </Button>
        <Button
          type="button"
          className={`btn-icon btn-icon-left ${activeView === "table" ? "btn-background-color active" : "btn-secondary"}`}
          style={{
            fontWeight: 600,
            borderRadius: "6px",
            padding: "8px 18px",
            fontSize: "13px",
            border: activeView === "table" ? "1px solid #9ee04e" : "1px solid #ced4da",
            backgroundColor: activeView === "table" ? "#b1fa63" : "#f8f9fa",
            color: "#052847",
            boxShadow: activeView === "table" ? "0 2px 4px rgba(0,0,0,0.08)" : "none",
          }}
          onClick={() => setActiveView("table")}
        >
          FBA Inventory Summary Table
        </Button>
      </div>

      {activeView === "alerts" ? (
        <InventoryAlertsPage profileId={props.profileId} />
      ) : (
        <Card className='card-of-problemarea mt-2 p-4'>
      <Row>
        <Col md={12} className="d-flex justify-content-end align-items-center mb-2">
          {snapshotText && (
            <i className="mr-3"><b>Inventory last updated at {snapshotText}</b></i>
          )}
          <Button
            className="btn-background-color"
            onClick={refreshInventory}
            disabled={loading}
            title="Refresh"
            style={{ padding: "6px 12px" }}
          >
            <FontAwesomeIcon icon={faSync} spin={loading} style={{ fontSize: "14px" }} />
          </Button>
          <Button
            className="btn-background-color ml-2"
            onClick={downloadInventory}
            disabled={loading || filteredInventory.length === 0}
            title="Download"
            style={{ padding: "6px 12px" }}
          >
            <FontAwesomeIcon icon={faDownload} style={{ fontSize: "14px" }} />
          </Button>
        </Col>
        <Col md={12} className="d-flex justify-content-end align-items-end">
        <h5 className="mb-0 mr-auto mb-2"><b className='mb-1'>Inventory Updates</b></h5>
          {renderDatePicker("Start Date", startDate, handleDateChange(setStartDate), {
            selectsStart: true, startDate, endDate, maxDate: endDate,
          })}
          {renderDatePicker("End Date", endDate, handleDateChange(setEndDate), {
            selectsEnd: true, startDate, endDate, minDate: startDate,
          })}
          <Button
            className="btn-background-color mr-2"
            onClick={handleSubmit}
            disabled={loading || !startDate || !endDate}
          >
            Submit
          </Button>
          <Input
            style={{ width: "220px" }}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search..."
          />
        </Col>
      </Row>
      <LoadingOverlay
        active={loading}
        styles={{
          overlay: (base) => ({
            ...base,
            background: '#f1f4f6',
            opacity: 1,
          }),
          content: (base) => ({
            ...base,
            color: '#000',
          }),
        }}
        spinner={<Loader active type="ball-pulse" />}
        text='Loading inventory...'
      >
        <Row>
          <Col>
            <ReactTable
              data={filteredInventory}
              columns={columns}
              className="-fixed -highlight -striped mt-3"
              getTheadProps={() => {
                return {
                  style: {
                    overflowY: "hidden",
                    background: "#B1FA63",
                    color: "#052847",
                  },
                };
              }}
              defaultPageSize={10}
              noDataText={"No Data Found."}
            />
          </Col>
        </Row>
      </LoadingOverlay>
        </Card>
      )}
    </div>
  )
}

export default StoreOverviewFBAInventory
